import { authService, UserResponse } from '../services/authService'
import adminSecurityService from '../services/adminSecurityService'
import { useAuthStore } from '../stores/authStore'
import { LoginPortal, saveLoginPreferences } from './loginPreferences'
import { applyAccountLocale, applyAccountVoicePreference } from './accountLocale'

export type AppRole = 'student' | 'admin' | 'tutor' | 'school' | 'affiliate'

export type PerformLoginResult =
  | { status: 'complete'; user: ReturnType<typeof mapUserResponseToAuthUser> }
  | { status: 'requires_2fa'; mfaToken: string }

export function mapUserResponseToAuthUser(latestUserData: UserResponse) {
  if (latestUserData.preferred_locale) {
    applyAccountLocale(latestUserData.preferred_locale)
  }
  if (latestUserData.voice_preference) {
    applyAccountVoicePreference(latestUserData.voice_preference)
  }
  return {
    id: latestUserData.id,
    email: latestUserData.email,
    firstName: latestUserData.first_name,
    lastName: latestUserData.last_name,
    role: latestUserData.role as AppRole,
    schoolName: latestUserData.school_name,
    schoolId: latestUserData.school_id,
    photoUrl: latestUserData.photo_url,
    photoFilename: latestUserData.photo_filename,
    photoSize: latestUserData.photo_size,
    photoMimeType: latestUserData.photo_mime_type,
    photoUploadedAt: latestUserData.photo_uploaded_at,
    photo: latestUserData.photo,
    timezone: latestUserData.timezone,
    preferredLocale: latestUserData.preferred_locale || undefined,
    voicePreference: latestUserData.voice_preference || undefined,
    lastSeen: latestUserData.last_seen,
    emailVerified: latestUserData.email_verified,
    onboardingCompleted: latestUserData.onboarding_completed,
    needsOnboarding: latestUserData.needs_onboarding,
    hasGoogle: latestUserData.has_google,
  }
}

export async function performLogin(options: {
  portal: LoginPortal
  email: string
  password: string
  rememberMe: boolean
  requiredRole?: AppRole
  captchaToken?: string | null
}): Promise<PerformLoginResult> {
  const { portal, email, password, rememberMe, requiredRole, captchaToken } = options

  const loginResponse = await authService.login({
    email,
    password,
    remember_me: rememberMe,
    ...(captchaToken ? { captcha_token: captchaToken } : {}),
  })

  if (loginResponse.requires_2fa && loginResponse.mfa_token) {
    if (requiredRole && requiredRole !== 'admin') {
      throw new Error('Two-factor authentication is only supported for admin accounts.')
    }
    return { status: 'requires_2fa', mfaToken: loginResponse.mfa_token }
  }

  saveLoginPreferences(portal, { email, rememberMe })

  let userDataToUse: UserResponse
  try {
    userDataToUse = await authService.getCurrentUser()
  } catch {
    throw new Error('Login succeeded but failed to load your profile. Please try again.')
  }

  const userData = mapUserResponseToAuthUser(userDataToUse)

  if (requiredRole && userData.role !== requiredRole) {
    await authService.logout()
    if (requiredRole === 'affiliate') {
      throw new Error(
        userData.role === 'student'
          ? 'account_is_student'
          : `Access denied. This login is for affiliates only. Your role is: ${userData.role}`,
      )
    }
    const roleLabel =
      requiredRole === 'tutor'
        ? 'tutors'
        : requiredRole === 'admin'
          ? 'administrators'
          : requiredRole === 'school'
            ? 'schools'
            : 'students'
    throw new Error(
      `Access denied. This login is for ${roleLabel} only. Your role is: ${userData.role}`
    )
  }

  useAuthStore.getState().login(userData)
  return { status: 'complete', user: userData }
}

export function unwrapPerformLoginResult(result: PerformLoginResult) {
  if (result.status === 'requires_2fa') {
    throw new Error('Two-factor verification is required for this account.')
  }
  return result.user
}

export async function completeAdmin2faLogin(options: {
  portal: LoginPortal
  email: string
  rememberMe: boolean
  mfaToken: string
  code: string
}): Promise<ReturnType<typeof mapUserResponseToAuthUser>> {
  const { portal, email, rememberMe, mfaToken, code } = options

  const response = await adminSecurityService.verify2faLogin(mfaToken, code)
  if (!response.access_token) {
    throw new Error('Verification succeeded but no access token was returned.')
  }

  authService.setToken(response.access_token, rememberMe)
  saveLoginPreferences(portal, { email, rememberMe })

  const userDataToUse = response.user ?? (await authService.getCurrentUser())
  const userData = mapUserResponseToAuthUser(userDataToUse)

  if (userData.role !== 'admin') {
    await authService.logout()
    throw new Error('Access denied. This login is for administrators only.')
  }

  useAuthStore.getState().login(userData)
  return userData
}
